// Shared SEO data. Plain JS with no imports so it can be used both by the
// React app (SEO component) and by the build-time prerender script (Node).

export const SITE_URL = 'https://mrencoder.com';
export const SITE_NAME = 'Mr. Encoder';
export const LANGUAGES = ['en', 'tr', 'de', 'es'];
export const DEFAULT_LANGUAGE = 'en';

// Native names for the language switcher.
export const LANGUAGE_NAMES = {
  en: 'English',
  tr: 'Türkçe',
  de: 'Deutsch',
  es: 'Español',
};

// 1200x630 social preview image (public/og-image.png).
export const OG_IMAGE = `${SITE_URL}/og-image.png`;

export const OG_LOCALES = {
  en: 'en_US',
  tr: 'tr_TR',
  de: 'de_DE',
  es: 'es_ES',
};

// Tools are grouped into categories for navigation (home page, menu, footer, related links).
export const CATEGORIES = ['encoding', 'formatting', 'converters', 'developer', 'validators'];

// Single source of truth for every tool page: id is the i18n key, slug is the URL segment.
export const TOOLS = [
  { id: 'base64', slug: 'base64', category: 'encoding' },
  { id: 'urlEncoder', slug: 'url-encoder', category: 'encoding' },
  { id: 'jwt', slug: 'jwt', category: 'encoding' },
  { id: 'hashGenerator', slug: 'hash-generator', category: 'encoding' },
  { id: 'htmlEntities', slug: 'html-entities', category: 'encoding' },
  { id: 'jsonBeautifier', slug: 'json-beautifier', category: 'formatting' },
  { id: 'sqlFormatter', slug: 'sql-formatter', category: 'formatting' },
  { id: 'xmlBeautifier', slug: 'xml-beautifier', category: 'formatting' },
  { id: 'htmlBeautifier', slug: 'html-beautifier', category: 'formatting' },
  { id: 'jsonToClass', slug: 'json-to-class', category: 'converters' },
  { id: 'jsonYaml', slug: 'json-to-yaml', category: 'converters' },
  { id: 'jsonCsv', slug: 'json-to-csv', category: 'converters' },
  { id: 'timestampConverter', slug: 'timestamp-converter', category: 'converters' },
  { id: 'numberBaseConverter', slug: 'number-base-converter', category: 'converters' },
  { id: 'colorConverter', slug: 'color-converter', category: 'converters' },
  { id: 'uuidGenerator', slug: 'uuid-generator', category: 'developer' },
  { id: 'regexTester', slug: 'regex-tester', category: 'developer' },
  { id: 'cronParser', slug: 'cron-parser', category: 'developer' },
  { id: 'textComparer', slug: 'text-comparer', category: 'developer' },
  { id: 'tcknVkn', slug: 'tckn-vkn-validator', category: 'validators' },
  { id: 'ibanValidator', slug: 'iban-validator', category: 'validators' },
  { id: 'xsltViewer', slug: 'xslt-viewer', category: 'validators' },
];

export const getToolBySlug = (slug) => TOOLS.find((tool) => tool.slug === slug);

export const pagePath = (lang, slug) => (slug ? `/${lang}/${slug}` : `/${lang}`);

export const pageUrl = (lang, slug) => `${SITE_URL}${pagePath(lang, slug)}`;

/**
 * Builds everything needed for the <head> of a page.
 * @param {{ lang: string, toolId?: string, messages: any }} params
 *   messages is the full translation object for `lang`.
 */
export const buildPageSeo = ({ lang, toolId, messages }) => {
  const tool = toolId ? TOOLS.find((item) => item.id === toolId) : undefined;
  const seo = tool ? messages.seo.tools[tool.id] : messages.seo;
  const canonical = pageUrl(lang, tool?.slug);

  const alternates = LANGUAGES.map((code) => ({ hrefLang: code, href: pageUrl(code, tool?.slug) }));
  alternates.push({ hrefLang: 'x-default', href: pageUrl(DEFAULT_LANGUAGE, tool?.slug) });

  const jsonLd = [];

  if (tool) {
    const content = messages.content[tool.id];

    jsonLd.push({
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: messages.tools[tool.id].title,
      url: canonical,
      description: seo.description,
      applicationCategory: 'DeveloperApplication',
      operatingSystem: 'Any',
      browserRequirements: 'Requires JavaScript',
      inLanguage: lang,
      isAccessibleForFree: true,
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
      publisher: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
    });

    jsonLd.push({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: SITE_NAME, item: pageUrl(lang) },
        { '@type': 'ListItem', position: 2, name: messages.tools[tool.id].title, item: canonical },
      ],
    });

    if (content?.faq?.length) {
      jsonLd.push({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: content.faq.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      });
    }
  } else {
    jsonLd.push({
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: SITE_NAME,
      url: canonical,
      description: seo.description,
      inLanguage: lang,
    });

    jsonLd.push({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: TOOLS.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: messages.tools[item.id].title,
        url: pageUrl(lang, item.slug),
      })),
    });
  }

  return {
    lang,
    title: seo.title,
    description: seo.description,
    canonical,
    alternates,
    ogLocale: OG_LOCALES[lang],
    ogLocaleAlternates: LANGUAGES.filter((code) => code !== lang).map((code) => OG_LOCALES[code]),
    image: OG_IMAGE,
    jsonLd,
  };
};
